/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import _ from 'lodash';

import { addErrorMessage } from '@ibm-watson/shared/modules/ia/store/messages/actions';
import { CreateAsyncThunk } from '@ibm-watson/shared/ts/state';
import * as integrationServerApi from '../../api/integrations/integrationServer';
import { PHONE_CHANNEL_TYPE } from '../../util/channelTypes';
import * as messages from '../channels/messages';

interface connectToContactCenterProps {
  /**
   * Phone integration's integration_id value
   */
  integrationId: string;
  /**
   * arn is used for subscribe for just Amazon Connect
   */
  arn?: string;
  /**
   * access_key_id is used for subscribe for both Nice CXOne & Amazon Connect
   */
  access_key_id: string;
  /**
   * access_key_secret is used for subscribe for both Nice CXOne & Amazon Connect
   */
  access_key_secret: string;
  /**
   * type is either 'nice_cxone' or 'amazon_connect'
   */
  type: string;
}
export const connectToContactCenter = createAsyncThunk<
  any,
  connectToContactCenterProps,
  CreateAsyncThunk
>('integrations/phoneConnectToContactCenter', async params => {
  try {
    return integrationServerApi.subscribe(
      PHONE_CHANNEL_TYPE,
      params.integrationId,
      _.omit(params, ['integrationId'])
    );
  } catch (error) {
    throw error;
  }
});

/**
 * Resetting the phone connection state status when a
 * phone integration connection is deleted
 */
export const resetContactCenter = createAction(
  'integrations/resetPhoneConnectToContactCenter'
);

/**
 * Updates reducers when STT instances do exist
 * and do not need to be created to continue
 */
export const STTInstanceCreateNotRequired = createAction(
  'integrations/STTInstanceCreateNotRequired'
);

/**
 * Updates reducers when TTS instances do exist
 * and do not need to be created to continue
 */
export const TTSInstanceCreateNotRequired = createAction(
  'integrations/TTSInstanceCreateNotRequired'
);

/**
 * Updates reducers when STT instances do not exist
 * and must be created to continue
 */
export const STTInstanceCreateRequired = createAction(
  'integrations/STTInstanceCreateRequired'
);

/**
 * Updates reducers when TTS instances do not exist
 * and must be created to continue
 */
export const TTSInstanceCreateRequired = createAction(
  'integrations/TTSInstanceCreateRequired'
);

export const getIntelepeerNumber = createAsyncThunk<
  any,
  string,
  CreateAsyncThunk
>(
  'integrations/getPhoneIntelepeerNumber',
  async (integrationId, { dispatch }) => {
    try {
      let response;
      if (integrationId) {
        response = await integrationServerApi.subscribe(
          PHONE_CHANNEL_TYPE,
          integrationId,
          {}
        );
      }
      return { id: integrationId, ...response };
    } catch (error) {
      dispatch(
        addErrorMessage({
          message: messages.intelepeerCheckFail.failed,
          status: 'error',
          transactionId: error.transactionId
        })
      );
    }
  }
);
